"use client";

import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";
import {
  Search,
  SortDesc,
  LayoutGrid,
  List,
  X,
  Upload,
  ChevronDown,
  ChevronUp,
  Trash2,
  Check,
  RefreshCw,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import {
  PhotoshopLogo,
  IllustratorLogo,
  WordLogo,
  ExcelLogo,
  PowerPointLogo,
  PDFLogo,
} from "@/components/assets/logos";
import { v4 as uuidv4 } from "uuid";
import { AppFile } from "@/types";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
  DrawerHeader,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface ContentProps {
  files: AppFile[];
  setFiles: React.Dispatch<React.SetStateAction<AppFile[]>>;
  selectedFiles: string[];
  setSelectedFiles: (files: string[]) => void;
  onSave: () => Promise<void>;
  isSaving: boolean;
  onDelete: (publicIds: string[]) => Promise<void>;
  isLoading: boolean;
  disabledFiles?: string[];
  onDrawerStateChange?: (hidden: boolean) => void;
  onRefresh?: () => Promise<void>;
  onImageUpload?: (
    imageData: string,
    fileName?: string,
    subFolder?: string
  ) => Promise<string>;
  fileTypes?: string[];
}

export default function Media({
  files: initialFiles,
  setFiles: setInitialFiles,
  selectedFiles,
  setSelectedFiles,
  onSave,
  isSaving,
  onDelete,
  isLoading,
  disabledFiles,
  onDrawerStateChange,
  onRefresh,
  onImageUpload,
  fileTypes,
}: ContentProps) {
  // Convert any string dates to Date objects when files are first received
  const [files, setFiles] = useState<AppFile[]>(() =>
    initialFiles.map((file) => ({
      ...file,
      dateAdded:
        file.dateAdded instanceof Date
          ? file.dateAdded
          : new Date(file.dateAdded),
    }))
  );

  // Update parent files when our local state changes
  useEffect(() => {
    setInitialFiles(files);
  }, [files, setInitialFiles]);

  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [selectedFileDetails, setSelectedFileDetails] =
    useState<AppFile | null>(null);
  const [isAddingUrl, setIsAddingUrl] = useState(false);
  const [fileUrl, setFileUrl] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [sortDrawerOpen, setSortDrawerOpen] = useState(false);
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [drawerHidden] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<AppFile[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // File size limits (in bytes)
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB (Cloudinary limit)
  const MAX_TOTAL_SIZE = 45 * 1024 * 1024; // 45MB (below Next.js 50MB limit for safety)

  // Helper function to validate file size
  const validateFileSize = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      console.log(
        `File ${file.name} is too large: ${file.size} bytes (${formatFileSize(file.size)})`
      );
      toast({
        variant: "destructive",
        title: "File too large",
        description: `${file.name} is ${formatFileSize(file.size)}. Maximum file size is ${formatFileSize(MAX_FILE_SIZE)}.`,
      });
      return false;
    }
    return true;
  };

  // Helper function to validate total upload size
  const validateTotalSize = (files: File[]) => {
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > MAX_TOTAL_SIZE) {
      toast({
        variant: "destructive",
        title: "Upload too large",
        description: `Total upload size is ${formatFileSize(totalSize)}. Maximum total size is ${formatFileSize(MAX_TOTAL_SIZE)}.`,
      });
      return false;
    }
    return true;
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;

    // Validate total size first
    if (!validateTotalSize(droppedFiles)) {
      return;
    }

    const filteredFiles = droppedFiles.filter((file) => {
      console.log(
        `Validating dropped file: ${file.name}, size: ${file.size} bytes (${formatFileSize(file.size)})`
      );

      // Validate individual file size
      if (!validateFileSize(file)) {
        return false;
      }

      // If fileTypes is specified, check if the file type is allowed
      if (fileTypes && fileTypes.length > 0) {
        const extension = file.name.split(".").pop()?.toLowerCase() || "";
        if (!fileTypes.includes(extension)) {
          toast({
            variant: "destructive",
            title: "Invalid file type",
            description: `${file.name} is not a supported file type. Allowed types: ${fileTypes.join(", ")}.`,
          });
          return false;
        }
      }
      return true;
    });

    if (filteredFiles.length === 0) return;

    try {
      // Convert files to base64 immediately to avoid CSP issues
      const newFiles: AppFile[] = await Promise.all(
        filteredFiles.map(async (file) => {
          const base64Url = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
          });

          return {
            id: uuidv4(),
            name: file.name.split(".")[0],
            type: file.name.split(".").pop() || "",
            url: base64Url,
            size: formatFileSize(file.size),
            dateAdded: new Date(),
          };
        })
      );

      setPendingFiles((prev) => [...prev, ...newFiles]);
      setFiles((prevFiles) => [...prevFiles, ...newFiles]);

      if (newFiles.length > 0) {
        toast({
          title: "Files added",
          description: `${newFiles.length} file${newFiles.length === 1 ? "" : "s"} added successfully.`,
        });
      }
    } catch (error) {
      console.error("Error processing dropped files:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to process dropped files. Please try again.",
      });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    const dropZone = e.currentTarget;
    dropZone.classList.add("border-primary");
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    const dropZone = e.currentTarget;
    dropZone.classList.remove("border-primary");
  };

  const formatDateShort = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles) return;

    const selectedFilesArray = Array.from(selectedFiles);

    // Validate total size first
    if (!validateTotalSize(selectedFilesArray)) {
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const filteredFiles = selectedFilesArray.filter((file) => {
      console.log(
        `Validating selected file: ${file.name}, size: ${file.size} bytes (${formatFileSize(file.size)})`
      );

      // Validate individual file size
      if (!validateFileSize(file)) {
        return false;
      }

      // If fileTypes is specified, check if the file type is allowed
      if (fileTypes && fileTypes.length > 0) {
        const extension = file.name.split(".").pop()?.toLowerCase() || "";
        if (!fileTypes.includes(extension)) {
          toast({
            variant: "destructive",
            title: "Invalid file type",
            description: `${file.name} is not a supported file type. Allowed types: ${fileTypes.join(", ")}.`,
          });
          return false;
        }
      }
      return true;
    });

    // Reset the file input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    if (filteredFiles.length === 0) return;

    try {
      // Convert files to base64 immediately to avoid CSP issues
      const newFiles: AppFile[] = await Promise.all(
        filteredFiles.map(async (file) => {
          const base64Url = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
          });

          return {
            id: uuidv4(),
            name: file.name.split(".")[0],
            type: file.name.split(".").pop() || "",
            url: base64Url,
            size: formatFileSize(file.size),
            dateAdded: new Date(),
          };
        })
      );

      // Add to pendingFiles instead of files
      setPendingFiles((prev) => [...prev, ...newFiles]);
      setFiles((prev) => [...prev, ...newFiles]);

      if (newFiles.length > 0) {
        toast({
          title: "Files added",
          description: `${newFiles.length} file${newFiles.length === 1 ? "" : "s"} added successfully.`,
        });
      }
    } catch (error) {
      console.error("Error processing selected files:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to process selected files. Please try again.",
      });
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleAddFromUrl = () => {
    if (!fileUrl) return;

    const fileName = fileUrl.split("/").pop() || "file";
    const fileType = fileName.split(".").pop() || "";

    // Check if it's a Cloudinary URL
    const isCloudinaryUrl = fileUrl.includes("cloudinary.com");

    const newFile: AppFile = {
      id: uuidv4(),
      name: fileName.split(".")[0],
      type: fileType,
      url: isCloudinaryUrl ? fileUrl : URL.createObjectURL(new Blob([fileUrl])),
      size: "Unknown", // Size cannot be determined from URL directly
      dateAdded: new Date(),
    };

    setFiles((prevFiles) => [...prevFiles, newFile]);
    setFileUrl("");
    setIsAddingUrl(false);
  };

  // Add sorting function
  const getSortedFiles = (files: AppFile[]) => {
    const sortedFiles = [...files];

    switch (sortBy) {
      case "name":
        sortedFiles.sort((a, b) => {
          const comparison = a.name.localeCompare(b.name);
          return sortDirection === "asc" ? comparison : -comparison;
        });
        break;
      case "date":
        sortedFiles.sort((a, b) => {
          const comparison = b.dateAdded.getTime() - a.dateAdded.getTime();
          return sortDirection === "asc" ? -comparison : comparison;
        });
        break;
      case "size":
        sortedFiles.sort((a, b) => {
          // Convert size strings to numbers for comparison
          const getSize = (size: string | number) => {
            if (typeof size === "number") return size;
            const [value, unit] = size.split(" ");
            const multiplier =
              {
                Bytes: 1,
                KB: 1024,
                MB: 1024 * 1024,
                GB: 1024 * 1024 * 1024,
              }[unit] || 1;
            return parseFloat(value) * multiplier;
          };

          const comparison = getSize(b.size) - getSize(a.size);
          return sortDirection === "asc" ? -comparison : comparison;
        });
        break;
    }

    return sortedFiles;
  };

  // Update the filtered files logic to filter by file type as well
  const filteredFiles = getSortedFiles(
    files.filter((file) => {
      const matchesSearch = file.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

      // If fileTypes is specified, check if the file type is allowed
      const matchesFileType =
        !fileTypes ||
        fileTypes.length === 0 ||
        fileTypes.includes(file.type.toLowerCase());

      return matchesSearch && matchesFileType;
    })
  );

  // Add handler for sort changes with proper typing
  const handleSortChange = (newSortBy: "name" | "date" | "size") => {
    if (sortBy === newSortBy) {
      // Toggle direction if clicking same sort option
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(newSortBy);
      setSortDirection("desc"); // Default to descending for new sort option
    }
  };

  const handleDelete = async (fileIds: string[]) => {
    // Check if the files to delete are pending or Cloudinary files
    const filesToDelete = files.filter((f) => fileIds.includes(f.id));
    const isPendingFile = filesToDelete.some((file) =>
      pendingFiles.some((pFile) => pFile.id === file.id)
    );

    if (isPendingFile) {
      // Directly remove pending files without showing dialog
      setPendingFiles((prev) =>
        prev.filter((file) => !fileIds.includes(file.id))
      );
      setFiles((prev) => prev.filter((file) => !fileIds.includes(file.id)));
    } else {
      // Show dialog for Cloudinary files
      setSelectedFiles(fileIds);
      setShowDeleteDialog(true);
    }
  };

  const handleBulkDelete = async () => {
    try {
      // Check if files are pending or uploaded
      const filesToDelete = files.filter((f) => selectedFiles.includes(f.id));
      const pendingFilesToDelete = filesToDelete.filter((file) =>
        pendingFiles.some((pFile) => pFile.id === file.id)
      );
      const cloudinaryFiles = filesToDelete.filter((f) =>
        f.url.includes("cloudinary.com")
      );

      // Delete from Cloudinary if there are any Cloudinary files
      if (cloudinaryFiles.length > 0) {
        const publicIds = cloudinaryFiles.map((file) => {
          const urlParts = file.url.split("/");
          const filenamePart = urlParts[urlParts.length - 1];
          const publicId = filenamePart.split(".")[0];
          return `content/${publicId}`;
        });

        await onDelete(publicIds);
      }

      // Update local state
      const newFiles = files.filter((f) => !selectedFiles.includes(f.id));
      setFiles(newFiles);

      // Update pending files state
      if (pendingFilesToDelete.length > 0) {
        setPendingFiles((prev) =>
          prev.filter((file) => !selectedFiles.includes(file.id))
        );
      }

      // Show success toast
      toast({
        title: "Files deleted",
        description: `${filesToDelete.length} file${filesToDelete.length === 1 ? "" : "s"} deleted successfully.`,
      });

      setSelectedFiles([]);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error("Error deleting files:", error);

      toast({
        variant: "destructive",
        title: "Delete failed",
        description: "Failed to delete files. Please try again.",
      });
    }
  };

  const handleSave = async () => {
    try {
      let errorCount = 0;

      // If we have onImageUpload function, upload pending files to Cloudinary
      if (onImageUpload && pendingFiles.length > 0) {
        const updatedFiles = [...files];
        let successCount = 0;

        for (const file of pendingFiles) {
          if (file.url.startsWith("data:")) {
            try {
              // Upload to Cloudinary using the base64 data URL
              const cloudinaryUrl = await onImageUpload(file.url, file.name);

              // Update the file URL
              const fileIndex = updatedFiles.findIndex((f) => f.id === file.id);
              if (fileIndex !== -1) {
                updatedFiles[fileIndex].url = cloudinaryUrl;
                successCount++;
              }
            } catch (error) {
              console.error("❌ Error uploading to Cloudinary:", error);
              errorCount++;

              // Show specific error for this file
              let errorMessage = "Upload failed";
              if (error instanceof Error) {
                if (error.message.includes("File size too large")) {
                  errorMessage = "File too large for Cloudinary";
                } else if (error.message.includes("Unknown error occurred")) {
                  errorMessage = "Upload service error";
                } else {
                  errorMessage = error.message;
                }
              }

              toast({
                variant: "destructive",
                title: `Failed to upload ${file.name}`,
                description: errorMessage,
              });
            }
          }
        }

        // Update files with new URLs
        setFiles(updatedFiles);

        // Show summary toast for multiple files
        if (successCount > 0 && errorCount === 0) {
          toast({
            title: "Upload successful",
            description: `${successCount} file${successCount === 1 ? "" : "s"} uploaded successfully.`,
          });
        } else if (successCount > 0 && errorCount > 0) {
          toast({
            variant: "destructive",
            title: "Partial upload failure",
            description: `${successCount} uploaded, ${errorCount} failed. See individual messages above.`,
          });
        } else if (errorCount > 0) {
          toast({
            variant: "destructive",
            title: "Upload failed",
            description: `Failed to upload ${errorCount} file${errorCount === 1 ? "" : "s"}.`,
          });
        }
      }

      // Call the original onSave function
      await onSave();

      // Clear pendingFiles after successful upload
      setPendingFiles([]);

      // Show final success message if no errors occurred during the entire process
      if (pendingFiles.length > 0 && errorCount === 0) {
        toast({
          title: "Save completed",
          description: `All files saved successfully.`,
        });
      }
    } catch (error) {
      console.error("Error saving files:", error);

      let errorMessage = "Failed to save files. Please try again.";
      if (error instanceof Error) {
        if (
          error.message.includes("Body exceeded") ||
          error.message.includes("413")
        ) {
          errorMessage =
            "Upload too large. Please reduce file sizes or upload fewer files.";
        } else if (error.message.includes("Network")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        } else {
          errorMessage = error.message;
        }
      }

      toast({
        variant: "destructive",
        title: "Save failed",
        description: errorMessage,
      });
    }
  };

  // Only show save button if there are pending files
  const showSaveButton = pendingFiles.length > 0;

  const getButtonClassName = (mode: "grid" | "list") => {
    return mode === "grid"
      ? "absolute bottom-2 right-2 h-8 w-8 bg-background/50 hover:bg-background/80"
      : "h-8 w-8";
  };
  return (
    <div className='flex max-w-full overflow-hidden'>
      <div className='flex-1 min-w-0'>
        {/* Search and View Controls */}
        <div className='flex flex-col gap-2 mb-2'>
          {/* Search Bar and Controls */}
          <div className='flex gap-2 items-center w-full'>
            <div className='relative flex-1'>
              <Search className='absolute left-2 top-2.5 h-4 w-4 text-gray-500' />
              <Input
                placeholder='Search files...'
                className='pl-8'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {isMobile ? (
              <>
                <Drawer
                  open={sortDrawerOpen}
                  onOpenChange={(open) => {
                    setSortDrawerOpen(open);
                    onDrawerStateChange?.(open);
                  }}
                >
                  <DrawerTrigger asChild>
                    <Button variant='outline' size='icon'>
                      <SortDesc className='h-4 w-4' />
                    </Button>
                  </DrawerTrigger>

                  <DrawerContent className='h-fit max-h-[60vh] z-[102] pb-4'>
                    <DrawerHeader>
                      <DrawerTitle>Sort By</DrawerTitle>
                    </DrawerHeader>
                    <div className='mt-4 px-4'>
                      <div className='space-y-2'>
                        <Button
                          variant='ghost'
                          className='w-full justify-between'
                          onClick={() => {
                            handleSortChange("name");
                            setSortDrawerOpen(false);
                            onDrawerStateChange?.(false);
                          }}
                        >
                          Name
                          {sortBy === "name" &&
                            (sortDirection === "asc" ? (
                              <ChevronUp className='h-4 w-4' />
                            ) : (
                              <ChevronDown className='h-4 w-4' />
                            ))}
                        </Button>
                        <Button
                          variant='ghost'
                          className='w-full justify-between'
                          onClick={() => {
                            handleSortChange("date");
                            setSortDrawerOpen(false);
                            onDrawerStateChange?.(false);
                          }}
                        >
                          Date Added
                          {sortBy === "date" &&
                            (sortDirection === "asc" ? (
                              <ChevronUp className='h-4 w-4' />
                            ) : (
                              <ChevronDown className='h-4 w-4' />
                            ))}
                        </Button>
                        <Button
                          variant='ghost'
                          className='w-full justify-between'
                          onClick={() => {
                            handleSortChange("size");
                            setSortDrawerOpen(false);
                            onDrawerStateChange?.(false);
                          }}
                        >
                          Size
                          {sortBy === "size" &&
                            (sortDirection === "asc" ? (
                              <ChevronUp className='h-4 w-4' />
                            ) : (
                              <ChevronDown className='h-4 w-4' />
                            ))}
                        </Button>
                      </div>
                    </div>
                  </DrawerContent>
                </Drawer>

                <Button
                  variant='outline'
                  size='icon'
                  onClick={onRefresh}
                  disabled={isLoading}
                >
                  <RefreshCw
                    className={cn("h-4 w-4", isLoading && "animate-spin")}
                  />
                </Button>

                <Drawer
                  open={viewDrawerOpen}
                  onOpenChange={(open) => {
                    setViewDrawerOpen(open);
                    onDrawerStateChange?.(open);
                  }}
                >
                  <DrawerTrigger asChild>
                    <Button variant='outline' size='icon'>
                      {viewMode === "grid" ? (
                        <LayoutGrid className='h-4 w-4' />
                      ) : (
                        <List className='h-4 w-4' />
                      )}
                    </Button>
                  </DrawerTrigger>
                  <DrawerContent className='h-fit max-h-[60vh] z-[102] pb-4'>
                    <div className='mt-4 px-4'>
                      <h3 className='font-semibold text-2xl text-center mb-4'>
                        View Mode
                      </h3>
                      <div className='space-y-2'>
                        <Button
                          variant='ghost'
                          className='w-full justify-between'
                          onClick={() => {
                            setViewMode("grid");
                            setViewDrawerOpen(false);
                            onDrawerStateChange?.(false);
                          }}
                        >
                          Grid View
                          {viewMode === "grid" && <Check className='h-4 w-4' />}
                        </Button>
                        <Button
                          variant='ghost'
                          className='w-full justify-between'
                          onClick={() => {
                            setViewMode("list");
                            setViewDrawerOpen(false);
                            onDrawerStateChange?.(false);
                          }}
                        >
                          List View
                          {viewMode === "list" && <Check className='h-4 w-4' />}
                        </Button>
                      </div>
                    </div>
                  </DrawerContent>
                </Drawer>
              </>
            ) : (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant='outline' size='icon'>
                      <SortDesc className='h-4 w-4' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className='z-[210]'>
                    <DropdownMenuItem
                      onClick={() => handleSortChange("name")}
                      className='flex items-center justify-between'
                    >
                      Name{" "}
                      {sortBy === "name" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp className='h-4 w-4' />
                        ) : (
                          <ChevronDown className='h-4 w-4' />
                        ))}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleSortChange("date")}
                      className='flex items-center justify-between'
                    >
                      Date Added{" "}
                      {sortBy === "date" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp className='h-4 w-4' />
                        ) : (
                          <ChevronDown className='h-4 w-4' />
                        ))}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleSortChange("size")}
                      className='flex items-center justify-between'
                    >
                      Size{" "}
                      {sortBy === "size" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp className='h-4 w-4' />
                        ) : (
                          <ChevronDown className='h-4 w-4' />
                        ))}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  variant='outline'
                  size='icon'
                  onClick={onRefresh}
                  disabled={isLoading}
                >
                  <RefreshCw
                    className={cn("h-4 w-4", isLoading && "animate-spin")}
                  />
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant='outline' size='icon'>
                      {viewMode === "grid" ? (
                        <LayoutGrid className='h-4 w-4' />
                      ) : (
                        <List className='h-4 w-4' />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className='z-[210]'>
                    <DropdownMenuItem onClick={() => setViewMode("grid")}>
                      Grid View
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setViewMode("list")}>
                      List View
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>

          {/* Bulk Actions and Save Button */}
          <div className='flex gap-2 justify-end'>
            {selectedFiles.length > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant='outline' className='gap-2'>
                    Bulk Actions ({selectedFiles.length})
                    <ChevronDown className='h-4 w-4' />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-48 z-[210]'>
                  <div className='space-y-2'>
                    <Button
                      variant='ghost'
                      className='w-full justify-start text-destructive hover:text-destructive gap-2'
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      <Trash2 className='h-4 w-4' />
                      Delete Selected
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {showSaveButton && (
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <LoadingSpinner className='mr-2 h-4 w-4 animate-spin' />
                    Saving...
                  </>
                ) : (
                  "Save All"
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Drop Zone and Buttons */}
        <div className='mb-4'>
          {/* Drop Zone - Hidden on mobile */}
          <div
            className='hidden md:block border-2 border-dashed rounded-md p-4 mb-4 text-center transition-colors'
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <Upload className='mx-auto h-8 w-8 text-gray-400 mb-2' />
            <p className='text-sm text-gray-600 mb-3'>
              Drag and drop files here, or click to select files
            </p>
            <div className='flex justify-center gap-2'>
              <input
                type='file'
                ref={fileInputRef}
                className='hidden'
                onChange={handleFileSelect}
                multiple
                accept={
                  fileTypes && fileTypes.length > 0
                    ? fileTypes.map((type) => `.${type}`).join(",")
                    : undefined
                }
              />
              <Button onClick={() => fileInputRef.current?.click()}>
                Add File
              </Button>
              <Button variant='outline' onClick={() => setIsAddingUrl(true)}>
                Add from URL
              </Button>
            </div>
          </div>

          {/* Mobile Buttons - Shown only on mobile */}
          <div className='md:hidden grid grid-cols-2 gap-2'>
            <input
              type='file'
              ref={fileInputRef}
              className='hidden'
              onChange={handleFileSelect}
              multiple
              accept={
                fileTypes && fileTypes.length > 0
                  ? fileTypes.map((type) => `.${type}`).join(",")
                  : undefined
              }
            />
            <Button
              className='w-full'
              onClick={() => fileInputRef.current?.click()}
            >
              Add File
            </Button>
            <Button
              variant='outline'
              className='w-full'
              onClick={() => setIsAddingUrl(true)}
            >
              Add from URL
            </Button>
          </div>
        </div>

        {/* URL Input Dialog */}
        <Dialog open={isAddingUrl} onOpenChange={setIsAddingUrl}>
          <DialogContent
            className={cn(
              "sm:max-w-[425px] z-[103]",
              drawerHidden && "translate-y-full transition-transform"
            )}
          >
            <DialogHeader>
              <DialogTitle>Add file from URL</DialogTitle>
              <DialogDescription>
                Enter a URL to add a file from an external source.
              </DialogDescription>
            </DialogHeader>
            <div className='grid gap-4 py-4'>
              <Input
                placeholder='Enter file URL...'
                value={fileUrl}
                onChange={(e) => setFileUrl(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={() => setIsAddingUrl(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddFromUrl}>Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Files Grid/List */}
        <div className='flex-1'>
          <ScrollArea className='h-[400px] mb-4'>
            {isLoading ? (
              <div className='h-full flex items-center justify-center'>
                <LoadingSpinner className='mr-2 h-4 w-4 animate-spin' />
                <span>Loading media...</span>
              </div>
            ) : filteredFiles.length === 0 ? (
              <div className='h-full flex flex-col items-center justify-center text-gray-500 space-y-2'>
                <p>No content found.</p>
                <p className='text-sm'>
                  {searchQuery
                    ? "Try adjusting your search."
                    : "Start by adding your first file."}
                </p>
              </div>
            ) : (
              <div
                className={`
                  grid 
                  ${
                    viewMode === "grid"
                      ? "grid-cols-[repeat(auto-fill,minmax(250px,1fr))]"
                      : "grid-cols-1"
                  } gap-4
                  auto-rows-min
                `}
              >
                {filteredFiles.map((file) => {
                  const isDisabled = disabledFiles?.includes(file.id);
                  const isPending = pendingFiles.some(
                    (pFile) => pFile.id === file.id
                  );
                  return (
                    <Card
                      key={file.id}
                      className={`p-2 rounded-md ${
                        viewMode === "list"
                          ? "flex items-center gap-4"
                          : "relative"
                      } cursor-pointer hover:bg-primary ${
                        selectedFiles.includes(file.id) ? "border-primary" : ""
                      } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""} ${
                        isPending ? "border-primary border-2 text-primary" : ""
                      }`}
                      onClick={() => {
                        if (isDisabled) return;
                        if (selectedFiles.includes(file.id)) {
                          setSelectedFiles(
                            selectedFiles.filter((id) => id !== file.id)
                          );
                        } else {
                          setSelectedFiles([...selectedFiles, file.id]);
                        }
                      }}
                    >
                      <Checkbox
                        className={
                          viewMode === "grid"
                            ? "absolute top-4 left-4 z-10"
                            : ""
                        }
                        checked={selectedFiles.includes(file.id)}
                        disabled={isDisabled}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedFiles([...selectedFiles, file.id]);
                          } else {
                            setSelectedFiles(
                              selectedFiles.filter((id) => id !== file.id)
                            );
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div
                        className={`
                          ${
                            viewMode === "grid"
                              ? "h-48 w-48 mx-auto"
                              : "h-12 w-12"
                          }
                        `}
                      >
                        {file.type.toLowerCase() === "psd" ? (
                          <PhotoshopLogo
                            className={`h-full w-full rounded-md object-contain  ${
                              viewMode === "grid" ? "px-4 pt-6" : ""
                            }`}
                            alt={`${file.name} preview`}
                          />
                        ) : file.type.toLowerCase() === "ai" ? (
                          <IllustratorLogo
                            className={`h-full w-full rounded-md object-contain  ${
                              viewMode === "grid" ? "px-4 pt-6" : ""
                            }`}
                            alt={`${file.name} preview`}
                          />
                        ) : file.type.toLowerCase() === "pdf" ? (
                          <PDFLogo
                            className={`h-full w-full rounded-md object-contain  ${
                              viewMode === "grid" ? "px-4 pt-6" : ""
                            }`}
                            alt={`${file.name} preview`}
                          />
                        ) : ["doc", "docx"].includes(
                            file.type.toLowerCase()
                          ) ? (
                          <WordLogo
                            className={`h-full w-full rounded-md object-contain  ${
                              viewMode === "grid" ? "px-4 pt-6" : ""
                            }`}
                            alt={`${file.name} preview`}
                          />
                        ) : ["xls", "xlsx"].includes(
                            file.type.toLowerCase()
                          ) ? (
                          <ExcelLogo
                            className={`h-full w-full rounded-md object-contain  ${
                              viewMode === "grid" ? "px-4 pt-6" : ""
                            }`}
                            alt={`${file.name} preview`}
                          />
                        ) : ["ppt", "pptx"].includes(
                            file.type.toLowerCase()
                          ) ? (
                          <PowerPointLogo
                            className={`h-full w-full rounded-md object-contain  ${
                              viewMode === "grid" ? "px-4 pt-6" : ""
                            }`}
                            alt={`${file.name} preview`}
                          />
                        ) : (
                          <Image
                            src={file.url}
                            alt={file.name}
                            className='h-full w-full rounded-md object-cover'
                            width={0}
                            height={0}
                            sizes='100vw'
                            style={{ width: "100%", height: "100%" }}
                          />
                        )}
                      </div>
                      <div
                        className={`
                        ${
                          viewMode === "grid"
                            ? "mt-2"
                            : "flex-1 flex items-center gap-4"
                        }
                      `}
                      >
                        <div
                          className={`flex-1 ${
                            viewMode === "list" ? "max-w-80 xl:max-w-full" : ""
                          }`}
                        >
                          <p className='font-medium truncate'>{file.name}</p>
                          {viewMode === "list" && (
                            <p
                              className={`text-sm text-gray-500 ${
                                viewMode === "list" ? "hidden" : ""
                              }`}
                            >
                              {formatDateShort(file.dateAdded)}
                            </p>
                          )}
                        </div>
                        <p className='text-gray-500 w-20'>.{file.type}</p>
                        {viewMode === "list" && (
                          <>
                            <p className='text-gray-500 w-20'>{file.size}</p>
                            <p className='text-gray-500 w-32'>
                              {formatDateShort(file.dateAdded)}
                            </p>
                            <Button
                              variant='ghost'
                              size='icon'
                              className={getButtonClassName(viewMode)}
                              onClick={(e) => {
                                e.stopPropagation();
                                const publicId = file.url.includes(
                                  "cloudinary.com"
                                )
                                  ? file.url.split("/").pop()?.split(".")[0]
                                  : null;
                                if (publicId) {
                                  handleDelete([`content/${publicId}`]);
                                  setSelectedFiles(
                                    selectedFiles.filter((id) => id !== file.id)
                                  );
                                } else {
                                  // For non-cloudinary files, just remove from local state
                                  setFiles((prev) =>
                                    prev.filter((f) => f.id !== file.id)
                                  );
                                }
                              }}
                            >
                              <Trash2 className='h-4 w-4' />
                            </Button>
                          </>
                        )}
                      </div>
                      {viewMode === "grid" && (
                        <Button
                          variant='ghost'
                          size='icon'
                          className={getButtonClassName(viewMode)}
                          onClick={(e) => {
                            e.stopPropagation();
                            const publicId = file.url.includes("cloudinary.com")
                              ? file.url.split("/").pop()?.split(".")[0]
                              : null;
                            if (publicId) {
                              handleDelete([`content/${publicId}`]);
                              setSelectedFiles(
                                selectedFiles.filter((id) => id !== file.id)
                              );
                            } else {
                              // For non-cloudinary files, just remove from local state
                              setFiles((prev) =>
                                prev.filter((f) => f.id !== file.id)
                              );
                            }
                          }}
                        >
                          <Trash2 className='h-4 w-4' />
                        </Button>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>

      {/* Side Panel - Hidden on mobile */}
      {selectedFileDetails && (
        <div className='hidden md:block w-80 border-l border-muted pl-4 flex-shrink-0'>
          <div className='mb-4'>
            <Button
              variant='ghost'
              size='sm'
              className='float-right'
              onClick={() => {
                handleDelete([selectedFileDetails.id]);
                setSelectedFileDetails(null);
              }}
            >
              <X className='h-4 w-4' />
            </Button>
          </div>

          <div className='mb-4 h-64'>
            {selectedFileDetails.type.toLowerCase() === "psd" ? (
              <PhotoshopLogo
                className='w-full h-full rounded-md object-contain p-4'
                alt={`${selectedFileDetails.name} preview`}
              />
            ) : selectedFileDetails.type.toLowerCase() === "ai" ? (
              <IllustratorLogo
                className='w-full h-full rounded-md object-contain p-4'
                alt={`${selectedFileDetails.name} preview`}
              />
            ) : selectedFileDetails.type.toLowerCase() === "pdf" ? (
              <PDFLogo
                className='w-full h-full rounded-md object-contain p-4'
                alt={`${selectedFileDetails.name} preview`}
              />
            ) : ["doc", "docx"].includes(
                selectedFileDetails.type.toLowerCase()
              ) ? (
              <WordLogo
                className='w-full h-full rounded-md object-contain p-4'
                alt={`${selectedFileDetails.name} preview`}
              />
            ) : ["xls", "xlsx"].includes(
                selectedFileDetails.type.toLowerCase()
              ) ? (
              <ExcelLogo
                className='w-full h-full rounded-md object-contain p-4'
                alt={`${selectedFileDetails.name} preview`}
              />
            ) : ["ppt", "pptx"].includes(
                selectedFileDetails.type.toLowerCase()
              ) ? (
              <PowerPointLogo
                className='w-full h-full rounded-md object-contain p-4'
                alt={`${selectedFileDetails.name} preview`}
              />
            ) : (
              <Image
                src={selectedFileDetails.url}
                alt={selectedFileDetails.name}
                className='w-full h-full rounded-md object-contain bg-white'
                width={300}
                height={300}
                sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
              />
            )}
          </div>

          <div className='space-y-4'>
            <div>
              <h3 className='font-semibold text-lg break-words'>
                {selectedFileDetails.name}
              </h3>
              <p className='text-sm text-gray-500'>
                .{selectedFileDetails.type}
              </p>
            </div>

            <div className='space-y-2'>
              <div className='flex justify-between text-sm'>
                <span className='text-gray-500'>Size</span>
                <span>{selectedFileDetails.size}</span>
              </div>
              <div className='flex justify-between text-sm'>
                <span className='text-gray-500'>Added</span>
                <span>{formatDateShort(selectedFileDetails.dateAdded)}</span>
              </div>
              <div className='flex justify-between text-sm'>
                <span className='text-gray-500'>Type</span>
                <span>{selectedFileDetails.type.toUpperCase()}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete{" "}
              {selectedFiles.length} selected{" "}
              {selectedFiles.length === 1 ? "file" : "files"}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedFiles([])}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className='bg-destructive hover:bg-destructive/90'
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
